import { useQueryClient } from '@tanstack/react-query';
import {
  ROTATION_PLAN_GONE_MESSAGE,
  ROTATION_SAVE_ERROR_MESSAGE,
  ROTATION_ZONE_LOAD_ERROR_MESSAGE,
} from 'constants/messageConstants';
import { INPUTS_QUERY_KEY, ZONE_BY_ID_QUERY_KEY } from 'constants/queryKeys';
import { useCreateInput } from 'hooks/inputs/useCreateInput';
import { useUpdateInputHeader } from 'hooks/inputs/useUpdateInputHeader';
import { useUpsertInputTask } from 'hooks/inputs/useUpsertInputTask';
import { useFetchGAreaUnits } from 'hooks/useFetchGAreaUnits';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { useProductionTonneUnit } from 'hooks/useProductionTonneUnit';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { ICrop, IInput, IUnit, IVariety } from 'interfaces/IInput';
import {
  InputStepType,
  type IInputStep,
  type IInputStepSeed,
} from 'interfaces/IInputStep';
import {
  InputTaskCategory,
  InputTaskType,
  type IInputTask,
} from 'interfaces/IInputTask';
import type { IPaddock } from 'interfaces/IPaddock';
import type { IZone } from 'interfaces/IZone';
import { buildBlankInput } from 'pages/FarmsPage/CreatePlanModal/buildBlankInput';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import ZoneService from 'services/ZoneService';
import { resolveAreaUnit } from 'utils/inputDraftPatches';
import { getUserDisplayName } from 'utils/UserUtils';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import type { RootState } from '../../../../../redux/Store';
import { buildStepFromSelection } from 'pages/FarmsPage/Workspace/Tasks/stepFactory';
import { buildTask } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import { buildDefaultPlanTasks } from 'pages/FarmsPage/Workspace/Tasks/planCropDefaults';
import type { RotationMixSeed } from './computeRotationGrid';
import { resolvePlanStartDate } from './resolvePlanStartDate';

const ZONE_STALE_TIME_MS = 1000 * 60 * 5;
const EMPTY_G_UNITS: GUnit[] = [];

/** What the advisor set in a cell. Sowing values left empty are saved as empty. */
export interface RotationCellValues {
  crop: Pick<ICrop, 'cropName' | 'globalId'>;
  variety: IVariety | null;
  sowing: {
    rate: number | null;
    unit: IUnit | null;
    cost: number | null;
    /** Calendar date at UTC midnight, or null. */
    date: string | null;
    /**
     * A seed mix to sow, each seed with its own crop, variety, rate and cost
     * (rate, unit and cost above are then unused), or null for one seed in
     * the plan's crop and variety.
     */
    mix: RotationMixSeed[] | null;
  };
}

/** The cell being saved: a paddock, a season and the plan already there, if any. */
export interface RotationCellTarget {
  paddock: IPaddock;
  season: number;
  inputId: string | null;
}

/** A save that didn't go through, with a message the editor can show. */
export class RotationSaveError extends Error {}

const hasSowingValues = (sowing: RotationCellValues['sowing']) =>
  sowing.mix != null ||
  sowing.rate != null ||
  sowing.cost != null ||
  sowing.date != null;

const isSeedStep = (step: IInputStep): step is IInputStepSeed =>
  step.stepType === InputStepType.Seed;

const planSowingTasks = (input: IInput): IInputTask[] =>
  (input.tasks ?? []).filter(
    (task) =>
      task.taskType === InputTaskType.CropEstablishment &&
      task.category === InputTaskCategory.Plan
  );

const sameUnit = (a: IUnit | null, b: IUnit | null) =>
  (a?.unit ?? null) === (b?.unit ?? null) &&
  (a?.globalId ?? null) === (b?.globalId ?? null);

/** The fields a Rotation save writes on one Seed step. */
interface SeedFields {
  item: string;
  globalId: string | null;
  item2: string | null;
  globalId2: string | null;
  rate: number | null;
  appUnit: IUnit | null;
  cost: number | null;
}

const sameSeed = (step: IInputStepSeed, seed: SeedFields) =>
  step.item === seed.item &&
  (step.globalId ?? null) === seed.globalId &&
  (step.item2 ?? null) === seed.item2 &&
  (step.globalId2 ?? null) === seed.globalId2 &&
  (step.rate ?? null) === seed.rate &&
  (step.cost ?? null) === seed.cost &&
  sameUnit(step.appUnit, seed.appUnit);

/**
 * The seeds a save writes: a seed mix as it is, each seed keeping its own
 * crop and variety, or one seed in the plan's crop and variety.
 */
const seedsToWrite = (values: RotationCellValues): SeedFields[] =>
  values.sowing.mix
    ? values.sowing.mix.map((seed) => ({
        item: seed.cropName,
        globalId: seed.cropId,
        item2: seed.varietyName,
        globalId2: seed.varietyId,
        rate: seed.rate,
        appUnit: seed.unit,
        cost: seed.cost,
      }))
    : [
        {
          item: values.crop.cropName,
          globalId: values.crop.globalId ?? null,
          item2: values.variety?.varietyName ?? null,
          globalId2: values.variety?.globalId ?? null,
          rate: values.sowing.rate,
          appUnit: values.sowing.unit,
          cost: values.sowing.cost,
        },
      ];

/**
 * Saves one Rotation planner cell (OP-2501). An empty cell gets a new plan,
 * built the way Create Plan builds one, starting 1 January of the season.
 * A cell with a plan has its crop and variety updated in place — never a
 * second plan. Sowing details go on the plan's one Plan crop establishment
 * task, created when there isn't one. Its Seed steps are overwritten: one
 * seed carries the plan's crop and variety so the plan and the task match;
 * a seed mix keeps each seed's own. The task's other steps, Actual tasks and
 * the plan's other tasks are never touched.
 *
 * Saves run one after another for a cell (plan, then task), each written
 * into the org's inputs cache, so every view updates without a refetch.
 * A failure throws RotationSaveError with a message for the editor.
 */
export const useRotationCellSave = () => {
  const queryClient = useQueryClient();
  const activeOrganisationId = useSelector(
    (state: RootState) => state.activeOrganisationReducer.organisationId
  );
  const gOrgId =
    useSelector(
      (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
    ) || null;
  const { data: userProfile } = useFetchUserProfile();
  const { data: areaUnits = EMPTY_G_UNITS } = useFetchGAreaUnits(gOrgId);
  const { mutateAsync: createInput } = useCreateInput({ activeOrganisationId });
  const { mutateAsync: updateHeader } = useUpdateInputHeader({
    activeOrganisationId,
  });
  const { mutateAsync: upsertTask } = useUpsertInputTask({
    activeOrganisationId,
  });
  const { tonneUnit } = useProductionTonneUnit(gOrgId);

  const cachedInput = useCallback(
    (inputId: string): IInput | undefined =>
      queryClient
        .getQueryData<IInput[]>([INPUTS_QUERY_KEY, activeOrganisationId])
        ?.find((input) => input.id === inputId),
    [activeOrganisationId, queryClient]
  );

  const createPlan = useCallback(
    async (
      target: RotationCellTarget,
      crop: ICrop,
      orgId: string,
      sowingDate: string | null
    ): Promise<IInput> => {
      let zone: IZone;
      try {
        zone = await queryClient.fetchQuery({
          queryKey: [ZONE_BY_ID_QUERY_KEY, target.paddock.zoneId],
          queryFn: () => ZoneService.GetById(target.paddock.zoneId),
          staleTime: ZONE_STALE_TIME_MS,
        });
      } catch {
        throw new RotationSaveError(ROTATION_ZONE_LOAD_ERROR_MESSAGE);
      }
      const blank = buildBlankInput({
        paddock: target.paddock,
        zone,
        gUnits: areaUnits,
        orgId,
        userProfile,
      });
      const draft: IInput = {
        ...blank,
        crop,
        startDate: resolvePlanStartDate({
          season: target.season,
          currentStart: null,
          previousSowingDate: null,
          sowingDate,
        }),
        zone: blank.zone
          ? {
              ...blank.zone,
              areaUnit: resolveAreaUnit(blank.zone.areaUnit, areaUnits),
            }
          : blank.zone,
      };
      const response = await createInput(draft);
      const created = response?.data?.[0] ?? draft;
      // A new plan starts with crop establishment and production tasks in
      // its crop, as one made with Create plan does (OP-2536). The sowing
      // save below then fills in that crop establishment task rather than
      // adding a second one.
      let tasks: IInputTask[] = created.tasks ?? [];
      for (const task of buildDefaultPlanTasks(
        created,
        { crop, cropOption: null, tonneUnit, isResolving: false },
        userProfile
      )) {
        try {
          const taskResponse = await upsertTask({ inputId: created.id, task });
          tasks = taskResponse?.data?.[0]?.tasks ?? [...tasks, task];
        } catch {
          // The plan and its sowing matter more; the task can be added later.
        }
      }
      return { ...created, tasks };
    },
    [areaUnits, createInput, queryClient, tonneUnit, upsertTask, userProfile]
  );

  const saveSowing = useCallback(
    async (input: IInput, values: RotationCellValues) => {
      const tasks = planSowingTasks(input);
      // More than one sowing record is edited in Planning, not here.
      if (tasks.length > 1) return;
      const existing = tasks[0] ?? null;
      const existingSeeds = existing ? existing.steps.filter(isSeedStep) : [];
      // Nothing to record and no seed to keep in step with the plan's crop.
      if (existingSeeds.length === 0 && !hasSowingValues(values.sowing)) {
        return;
      }

      const wanted = seedsToWrite(values);
      const unchanged =
        existing &&
        existing.endDate === values.sowing.date &&
        existingSeeds.length === wanted.length &&
        existingSeeds.every((step, index) => sameSeed(step, wanted[index]));
      if (unchanged) return;

      const base =
        existing ??
        buildTask(
          input,
          'cropEstablishment',
          InputTaskCategory.Plan,
          userProfile
        );
      const task: IInputTask = {
        ...base,
        endDate: values.sowing.date,
        endDateHasDay: values.sowing.date != null,
      };
      // Existing Seed steps are reused in order, so their ids and other
      // fields stay; any left over are removed and any missing are added.
      const seeds: IInputStep[] = wanted.map((seed, index) => {
        const current = existingSeeds[index];
        if (current) return { ...current, ...seed };
        return buildStepFromSelection(
          task,
          {
            stepType: InputStepType.Seed,
            item: seed.item,
            globalId: seed.globalId,
            item2: seed.item2,
            globalId2: seed.globalId2,
            rate: seed.rate,
            unit: seed.appUnit,
            cost: seed.cost,
          },
          userProfile
        );
      });
      let nextSeed = 0;
      const steps = task.steps.flatMap((step) => {
        if (!isSeedStep(step)) return [step];
        const seed = seeds[nextSeed];
        nextSeed += 1;
        return seed ? [seed] : [];
      });
      task.steps = [...steps, ...seeds.slice(nextSeed)];

      await upsertTask({ inputId: input.id, task });
    },
    [upsertTask, userProfile]
  );

  return useCallback(
    async (target: RotationCellTarget, values: RotationCellValues) => {
      if (!activeOrganisationId) {
        throw new RotationSaveError(ROTATION_SAVE_ERROR_MESSAGE);
      }
      const crop: ICrop = { ...values.crop, variety: values.variety };
      try {
        let input: IInput;
        if (target.inputId) {
          const current = cachedInput(target.inputId);
          if (!current) throw new RotationSaveError(ROTATION_PLAN_GONE_MESSAGE);
          const cropChanged =
            current.crop?.cropName !== crop.cropName ||
            current.crop?.globalId !== crop.globalId ||
            (current.crop?.variety?.varietyName ?? null) !==
              (crop.variety?.varietyName ?? null) ||
            (current.crop?.variety?.globalId ?? null) !==
              (crop.variety?.globalId ?? null);
          const currentSowing = planSowingTasks(current);
          const startDate = resolvePlanStartDate({
            season: target.season,
            currentStart: current.startDate,
            previousSowingDate:
              currentSowing.length === 1 ? currentSowing[0].endDate : null,
            sowingDate: values.sowing.date,
          });
          input = current;
          if (cropChanged || startDate) {
            const response = await updateHeader({
              ...current,
              crop,
              startDate: startDate ?? current.startDate,
              dateModified: new Date().toISOString(),
              modifierId: userProfile?.id ?? current.modifierId,
              snapModifier:
                getUserDisplayName(
                  userProfile?.firstName,
                  userProfile?.lastName
                ) || current.snapModifier,
            });
            input = { ...(response?.data?.[0] ?? current), crop };
            // The task list to build on is the cached one (see useUpdateInputHeader).
            input = { ...input, tasks: cachedInput(current.id)?.tasks ?? [] };
          }
        } else {
          input = await createPlan(
            target,
            crop,
            activeOrganisationId,
            values.sowing.date
          );
        }
        await saveSowing(input, values);
      } catch (error) {
        if (error instanceof RotationSaveError) throw error;
        throw new RotationSaveError(
          getApiErrorMessage(error, ROTATION_SAVE_ERROR_MESSAGE)
        );
      }
    },
    [
      activeOrganisationId,
      cachedInput,
      createPlan,
      saveSowing,
      updateHeader,
      userProfile?.firstName,
      userProfile?.id,
      userProfile?.lastName,
    ]
  );
};
